import { useRef, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Share2, Volume2, VolumeX, Play, Trash2, Loader2, Hand, X, Download, Pencil } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { Button } from '../ui/button';
import { useToast } from '../ui/use-toast';
import { makeLogger } from '../../utils/logger';
import type { ClimbVideo } from '../../types/climb-video';

const log = makeLogger('ClimbVideoViewer');

// A horizontal drag beyond this many px counts as a swipe; below it, a tap.
const SWIPE_THRESHOLD = 50;
const TAP_THRESHOLD = 10;

// Usable length for the seek bar. A fragmented MP4 (e.g. an in-browser MediaRecorder
// upload served before transcoding finishes) reports `duration === Infinity`; seeking
// against that jumps to the end and, with `loop`, restarts the video. Prefer the real
// duration, fall back to a *finite* seekable range, and otherwise return 0 so the seek
// is skipped entirely — never letting a bad value restart playback.
function effectiveDuration(v: HTMLVideoElement): number {
  if (Number.isFinite(v.duration) && v.duration > 0) return v.duration;
  if (v.seekable.length > 0) {
    const end = v.seekable.end(v.seekable.length - 1);
    if (Number.isFinite(end) && end > 0) return end;
  }
  return 0;
}

interface ClimbVideoViewerProps {
  video: ClimbVideo;
  muted: boolean;
  isOwner: boolean;
  /** Sharing is limited to logged-in users. */
  canShare: boolean;
  canPrev: boolean;
  canNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  onToggleMute: () => void;
  onClose: () => void;
  onDelete: (videoId: string) => Promise<void>;
  /** Owner-only: opens the share page with this video pre-selected for editing. */
  onEdit: () => void;
  shareTitle: string;
  /** Resume position (seconds) when opened from the feed, so playback doesn't restart. */
  initialTime?: number;
  // Engagement bar (below the video): likes (raised hand + count → likers dialog) and shares.
  likedByMe: boolean;
  sharesCount: number;
  onToggleLike: () => void;
  onRegisterShare: () => void;
}

/**
 * Fullscreen, controls-free video viewer rendered as a fixed overlay (not the
 * shadcn Dialog, so there's no built-in close button). On the video itself a tap
 * toggles play/pause and a horizontal swipe moves to the prev/next video; a thin
 * draggable bar at the bottom seeks (and never triggers a swipe, since it's a
 * separate element on top of the video). Owners get a delete footer.
 */
export function ClimbVideoViewer({
  video, muted, isOwner, canShare, canPrev, canNext, onPrev, onNext, onToggleMute, onClose, onDelete, onEdit, shareTitle, initialTime,
  likedByMe, sharesCount, onToggleLike, onRegisterShare,
}: ClimbVideoViewerProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [likersOpen, setLikersOpen] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);
  const swipeStartRef = useRef<{ x: number; y: number } | null>(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0); // [0,1]
  const [isDeleting, setIsDeleting] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Lock body scroll while open + close on Escape.
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  // Keep the <video> muted state in sync with the shared toggle.
  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = muted;
  }, [muted]);

  // Reset playback UI when navigating to another video.
  useEffect(() => {
    setProgress(0);
    setIsPlaying(true);
    setHasError(false);
  }, [video.id]);

  // A decode/source failure (e.g. a codec the device can't play) leaves a black
  // square; surface it instead, and log the MediaError so we can see what fails.
  const handleError = useCallback(() => {
    const err = videoRef.current?.error;
    log.error('playback failed', { code: err?.code, message: err?.message, src: video.video_url });
    setHasError(true);
  }, [video.video_url]);

  // Track playback position for the seek bar.
  useEffect(() => {
    let raf = 0;
    const tick = () => {
      const v = videoRef.current;
      const dur = v ? effectiveDuration(v) : 0;
      if (v && dur > 0 && !draggingRef.current) setProgress(v.currentTime / dur);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  // Resume from the feed's playback position the first time this video's metadata is
  // ready. Without this, opening a feed video restarts it from the beginning.
  const resumedForRef = useRef<string | null>(null);
  const handleLoadedMetadata = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (initialTime == null || resumedForRef.current === video.id) return;
    resumedForRef.current = video.id;
    const dur = effectiveDuration(v);
    if (Number.isFinite(initialTime) && dur > 0) {
      v.currentTime = Math.min(initialTime, dur - 0.05);
    }
  }, [initialTime, video.id]);

  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play().then(() => setIsPlaying(true)).catch(() => {});
    } else {
      v.pause();
      setIsPlaying(false);
    }
  }, []);

  // ── Tap vs swipe on the gesture layer ──────────────────────────────────────
  const handleGesturePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    swipeStartRef.current = { x: e.clientX, y: e.clientY };
    // Capture so the gesture still resolves if the finger drifts over an overlay.
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const handleGesturePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const start = swipeStartRef.current;
    swipeStartRef.current = null;
    if (!start) return;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;

    if (Math.abs(dx) < TAP_THRESHOLD && Math.abs(dy) < TAP_THRESHOLD) {
      togglePlay();
      return;
    }
    if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
      if (dx < 0) { if (canNext) onNext(); } // swipe left → next
      else if (canPrev) onPrev();            // swipe right → previous
    }
  };

  // ── Seek bar ────────────────────────────────────────────────────────────────
  const seekTo = (clientX: number) => {
    const track = trackRef.current;
    const v = videoRef.current;
    if (!track || !v) return;
    const dur = effectiveDuration(v);
    if (dur <= 0) return;
    const rect = track.getBoundingClientRect();
    const frac = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    v.currentTime = frac * dur;
    setProgress(frac);
  };

  const handleDeleteClick = async () => {
    if (!window.confirm(t('climbVideos.deleteConfirm'))) return;
    setIsDeleting(true);
    try {
      await onDelete(video.id);
      // The feed advances/closes the viewer on success.
    } catch (err) {
      log.error('delete failed:', err);
      toast({ title: t('climbVideos.deleteError'), variant: 'destructive' });
      setIsDeleting(false);
    }
  };

  // Share button: only ever shares the link to this video, never the file itself.
  const handleShare = async () => {
    // Record the share (the user clicked the share button) regardless of the sheet outcome.
    onRegisterShare();
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ url, title: shareTitle });
        return;
      }
    } catch {
      // user cancelled or unsupported — fall through to clipboard
    }
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: t('climbVideos.linkCopied') });
    } catch {
      /* ignore */
    }
  };

  // Owner-only: shares the actual video file via the OS share sheet, falling back
  // to a plain download when the platform can't share files. Distinct from the
  // link-only share button above.
  const handleDownload = async () => {
    if (!video.video_url) return;
    try {
      const res = await fetch(video.video_url);
      const blob = await res.blob();
      const file = new File([blob], `${shareTitle || 'climb'}-video.mp4`, { type: blob.type || 'video/mp4' });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: shareTitle });
        return;
      }
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      log.error('video download failed:', err);
      toast({ title: t('climbVideos.downloadError'), variant: 'destructive' });
    }
  };

  // Portal to <body> so the overlay escapes any transformed/stacking ancestor.
  // It starts below the app navbar (h-16) so the real navbar — and its breadcrumb —
  // stays visible and is what the user uses to navigate / close.
  return createPortal(
    // data-fullscreen-overlay tells the page's climb swipe-navigation (ClimbNavigation)
    // to ignore touches in here, so seeking/swiping the video never changes the climb.
    <div data-fullscreen-overlay className="fixed inset-x-0 bottom-0 top-16 z-[100] bg-black">
      <video
        key={video.id}
        ref={videoRef}
        src={video.video_url ?? undefined}
        poster={video.poster_url ?? undefined}
        className="absolute inset-0 h-full w-full object-contain"
        autoPlay
        loop
        playsInline
        muted={muted}
        onLoadedMetadata={handleLoadedMetadata}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onError={handleError}
      />

      {/* Playback error fallback — shown instead of a silent black square */}
      {hasError && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center px-6 text-center">
          <p className="text-sm font-medium text-white/90">{t('climbVideos.playbackError')}</p>
        </div>
      )}

      {/* Full-screen gesture layer (under the controls): tap = play/pause, horizontal swipe = prev/next */}
      <div
        data-testid="video-gesture"
        className="absolute inset-0 touch-none"
        onPointerDown={handleGesturePointerDown}
        onPointerUp={handleGesturePointerUp}
      />

      {/* Paused indicator */}
      {!isPlaying && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="rounded-full bg-black/40 p-4">
            <Play className="h-10 w-10 text-white" fill="currentColor" />
          </div>
        </div>
      )}

      {/* Bottom stack: controls, seek bar, optional owner footer.
          The wrapper ignores pointers (taps fall through to the gesture layer);
          only the real controls below re-enable them. */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent pt-10">
        {/* Audio toggle */}
        <div className="pointer-events-auto flex items-center justify-end gap-2 px-3 pb-2">
          <button
            type="button"
            onClick={onToggleMute}
            aria-label={muted ? t('climbVideos.unmute') : t('climbVideos.mute')}
            className="rounded-full bg-black/50 p-2 text-white hover:bg-black/70"
          >
            {muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
          </button>
        </div>

        {/* Thin draggable seek bar — sits above the gesture layer, so it seeks and never swipes */}
        <div
          ref={trackRef}
          className="pointer-events-auto flex touch-none items-end py-2"
          onPointerDown={e => { e.stopPropagation(); draggingRef.current = true; e.currentTarget.setPointerCapture(e.pointerId); seekTo(e.clientX); }}
          onPointerMove={e => { if (draggingRef.current) seekTo(e.clientX); }}
          onPointerUp={() => { draggingRef.current = false; }}
          onPointerCancel={() => { draggingRef.current = false; }}
        >
          <div className="relative h-1 w-full bg-white/25">
            <div className="absolute inset-y-0 left-0 bg-white" style={{ width: `${progress * 100}%` }} />
          </div>
        </div>

        {/* Engagement bar — two halves: like (raised hand + count → likers) | share (+ count) */}
        <div className="pointer-events-auto flex items-stretch border-t border-white/10 bg-black/40 text-white">
          {/* Left: like */}
          <div className="flex flex-1 items-center justify-center gap-3 py-3">
            {canShare && (
              <button
                type="button"
                onClick={onToggleLike}
                aria-label={t('climbVideos.like')}
                className={`transition-colors hover:opacity-80 ${likedByMe ? 'text-yellow-400' : 'text-white'}`}
              >
                <Hand className="h-6 w-6" />
              </button>
            )}
            <button
              type="button"
              onClick={() => setLikersOpen(true)}
              aria-label={t('climbVideos.likesCount')}
              className="text-sm font-semibold tabular-nums hover:underline"
            >
              {video.likers.length}
            </button>
          </div>

          <div className="w-px bg-white/15" />

          {/* Right: share */}
          <div className="flex flex-1 items-center justify-center gap-3 py-3">
            {canShare && (
              <button
                type="button"
                onClick={handleShare}
                aria-label={t('climbVideos.share')}
                className="text-white transition-colors hover:opacity-80"
              >
                <Share2 className="h-6 w-6" />
              </button>
            )}
            <span className="text-sm font-semibold tabular-nums">{sharesCount}</span>
          </div>
        </div>

        {/* Owner-only footer: edit (share page) + download (share the actual file) + delete */}
        {isOwner && (
          <div className="pointer-events-auto flex flex-wrap items-center justify-center gap-2 border-t border-white/10 bg-black/80 px-3 py-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={onEdit}
              className="text-white hover:bg-white/10"
            >
              <Pencil className="mr-2 h-4 w-4" />
              {t('climbVideos.edit')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDownload}
              className="text-white hover:bg-white/10"
            >
              <Download className="mr-2 h-4 w-4" />
              {t('climbVideos.download')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDeleteClick}
              disabled={isDeleting}
              className="text-red-400 hover:bg-red-500/10 hover:text-red-300"
            >
              {isDeleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
              {t('climbVideos.delete')}
            </Button>
          </div>
        )}
      </div>

      {/* Likers dialog — opened from the likes count */}
      {likersOpen && (
        <div
          className="absolute inset-0 z-10 flex items-end justify-center bg-black/70 sm:items-center"
          onClick={() => setLikersOpen(false)}
        >
          <div
            className="max-h-[70%] w-full max-w-sm overflow-y-auto rounded-t-2xl bg-background p-4 sm:rounded-2xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-base font-semibold">{t('climbVideos.likesTitle')}</h3>
              <button
                type="button"
                onClick={() => setLikersOpen(false)}
                aria-label={t('climbVideos.close')}
                className="rounded-full p-1 hover:bg-accent"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            {video.likers.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">{t('climbVideos.noLikes')}</p>
            ) : (
              <ul className="space-y-3">
                {video.likers.map(liker => (
                  <li key={liker.id} className="flex items-center gap-3">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={liker.avatar_small_url ?? undefined} alt={liker.username} />
                      <AvatarFallback className="text-xs">{liker.username.charAt(0).toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <span className="text-sm font-medium">{liker.username}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
}

interface VideoUserBadgeProps {
  user: ClimbVideo['user'];
}

/** Avatar + username overlay used on top of each video. */
export function VideoUserBadge({ user }: VideoUserBadgeProps) {
  return (
    <div className="flex items-center gap-2">
      <Avatar className="h-8 w-8 ring-2 ring-white/70">
        <AvatarImage src={user.avatar_small_url ?? undefined} alt={user.username} />
        <AvatarFallback className="text-xs">{user.username.charAt(0).toUpperCase()}</AvatarFallback>
      </Avatar>
      <span className="text-sm font-semibold text-white drop-shadow">{user.username}</span>
    </div>
  );
}
